import { events } from "@constants/tracking";
import { emptyItemTypeDetailStats } from "@memorang/helpers/report";
import {
	SessionReportItemTypeDetailDialog,
	type SessionReportItemTypeDetailSelection,
} from "@memorang/ui/components/SessionReport/SessionReportItemTypeDetailDialog";
import { useCallback, useMemo, useState } from "react";
import type { GoToReviewSession } from "./useGoToReviewSession";
import type { TrackReportEvent } from "./useReportAnalytics";

type Options = {
	onReview: GoToReviewSession;
	isReviewLoading?: boolean;
	trackReportEvent: TrackReportEvent;
};

export const useSessionReportItemTypeDetailDialog = ({
	onReview,
	isReviewLoading = false,
	trackReportEvent,
}: Options) => {
	const [selection, setSelection] =
		useState<SessionReportItemTypeDetailSelection | null>(null);

	const handleQuestionTypeRowPress = useCallback(
		(nextSelection: SessionReportItemTypeDetailSelection) => {
			trackReportEvent(events.sessionReportCardClicked, {
				type: nextSelection.questionType,
				skill: nextSelection.skill,
				numItems: nextSelection.numItems,
			});
			setSelection(nextSelection);
		},
		[trackReportEvent],
	);

	const handleClose = useCallback(() => {
		setSelection(null);
	}, []);

	const handleReview = useCallback(async () => {
		if (selection == null) {
			return;
		}
		const items = selection.reviewItems;
		await onReview(items, {
			source: "item_type_dialog",
			itemType: selection.questionType,
			skill: selection.skill,
		});
		setSelection(null);
	}, [onReview, selection]);

	const itemTypeDetailDialog = useMemo(
		() => (
			<SessionReportItemTypeDetailDialog
				visible={selection != null}
				onClose={handleClose}
				questionType={selection?.questionType ?? ""}
				masteryLevel={selection?.masteryLevel}
				levels={selection?.levels}
				numItems={selection?.numItems ?? 0}
				stats={selection?.stats ?? emptyItemTypeDetailStats}
				onReview={handleReview}
				loading={isReviewLoading}
			/>
		),
		[handleClose, handleReview, isReviewLoading, selection],
	);

	return {
		onSelectQuestionType: handleQuestionTypeRowPress,
		itemTypeDetailDialog,
	};
};
